/**
 * vj-cart.js — Cart helper aligned with Horizon theme's event system.
 *
 * Horizon drives cart UI through a single DOM event on `document`:
 *   document.dispatchEvent(new CustomEvent('cart:update', {
 *     bubbles: true,
 *     detail: { resource, sourceId, data: { source, itemCount, productId, sections } }
 *   }))
 *
 *   - <cart-icon> increments/sets the bubble count based on `data.source`
 *   - <cart-items-component> morphs its section from `data.sections[sectionId]`
 *   - <cart-drawer-component> opens if it has `auto-open`
 *
 * This file provides:
 *   window.VJCart.add(variantId, qty) — POST /cart/add.js + dispatch cart:update
 *   A fetch hook that recovers when third-party cart writes forget sections/events
 *   A global delegated handler that replaces Horizon's broken quick-add button
 *     with our own: single-variant → add directly, multi-variant → VJ modal.
 */
// ---- Legacy locale-prefix rescue (2026-07-20 Markets migration) ----------
// The /en-ca and /fr-ca subfolders died when the Canada presence was
// re-anchored to the root domain (EN → /, FR → /fr); Shopify 404s them with
// no redirect. Old links survive in sent Klaviyo emails, live notification
// templates, and Google's index — rewrite them client-side on the 404 page.
// vj-cart.js is the one asset theme.liquid loads globally, hence it lives
// here. Guard is inert on every live path (those prefixes can no longer
// occur), so it costs one regex test per page load.
(function () {
  var m = location.pathname.match(/^\/(fr-ca|en-ca)(\/.*)?$/i);
  if (m) {
    var rest = m[2] || '/';
    var target = (m[1].toLowerCase() === 'fr-ca' ? '/fr' : '') + rest;
    location.replace(target + location.search + location.hash);
  }
})();
(function () {
  'use strict';

  if (window.VJCart) return;

  const _originalFetch = window.fetch.bind(window);

  function getCartItemsSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach(el => {
      const sid = el.dataset && el.dataset.sectionId;
      if (sid) ids.add(sid);
    });
    return Array.from(ids);
  }

  function openCartDrawer() {
    const drawer = document.querySelector(
      'cart-drawer-component, cart-drawer, cart-notification, [data-cart-drawer], #CartDrawer, #cart-drawer'
    );
    if (!drawer) return;
    if (typeof drawer.open === 'function') { drawer.open(); return; }
    if (typeof drawer.showDialog === 'function') { drawer.showDialog(); return; }
    if (typeof drawer.show === 'function') { drawer.show(); return; }
    drawer.removeAttribute('hidden');
    drawer.classList.add('active', 'is-open', 'open');
    drawer.setAttribute('open', '');
  }

  function dispatchCartUpdate(resource, data) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: resource || {},
          sourceId: 'vj-cart',
          data: data || {},
        },
      })
    );
  }

  async function add(variantId, quantity) {
    const qty = Number(quantity) || 1;
    const fd = new FormData();
    fd.append('id', String(variantId));
    fd.append('quantity', String(qty));

    const sectionIds = getCartItemsSectionIds();
    if (sectionIds.length) {
      fd.append('sections', sectionIds.join(','));
      fd.append('sections_url', window.location.pathname);
    }

    const res = await _originalFetch('/cart/add.js', {
      method: 'POST',
      body: fd,
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.description || data.message || 'Add to cart failed');
      err.data = data;
      throw err;
    }

    dispatchCartUpdate(data, {
      source: 'product-form-component',
      itemCount: qty,
      variantId: data.variant_id,
      productId: data.product_id,
      sections: data.sections,
    });

    openCartDrawer();
    return data;
  }

  async function refresh() {
    const sectionIds = getCartItemsSectionIds();
    let sections = null;
    if (sectionIds.length) {
      try {
        const url = window.location.pathname + '?sections=' + encodeURIComponent(sectionIds.join(','));
        const res = await _originalFetch(url, { headers: { Accept: 'application/json' } });
        if (res.ok) sections = await res.json();
      } catch (_e) {}
    }
    let cart = null;
    try {
      cart = await _originalFetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json());
    } catch (_e) {}
    dispatchCartUpdate(cart, {
      source: 'vj-cart-refresh',
      itemCount: cart ? cart.item_count : 0,
      sections: sections || undefined,
    });
  }

  window.VJCart = { add, refresh, openCartDrawer };

  // ── Safety net for third-party /cart writes ───────────────────────────────
  let lastRefresh = 0;
  function scheduleRefresh() {
    const now = Date.now();
    if (now - lastRefresh < 400) return;
    lastRefresh = now;
    setTimeout(() => refresh().catch(() => {}), 80);
  }

  window.fetch = function (input, init) {
    const urlStr =
      typeof input === 'string'
        ? input
        : (input && (input.url || String(input))) || '';
    const isCartWrite = /\/cart\/(add|change|update|clear)(\.js|\.json)?($|\?)/.test(urlStr);
    const method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    const p = _originalFetch(input, init);
    if (isCartWrite && method === 'POST') {
      let hasSections = false;
      try {
        const body = init && init.body;
        if (body instanceof FormData) {
          hasSections = body.has('sections');
        } else if (typeof body === 'string') {
          hasSections = /(^|&)sections=/.test(body);
        } else if (body && typeof body === 'object' && 'sections' in body) {
          hasSections = true;
        }
      } catch (_e) {}
      if (!hasSections) {
        p.then(() => scheduleRefresh()).catch(() => {});
      }
    }
    return p;
  };

  // ── VJ quick-add override ────────────────────────────────────────────────
  // Horizon's native quick-add modal has been flaky (wouldn't open on click).
  // We replace it: intercept clicks on any quick-add button, fetch the product
  // JSON, then either add directly (single variant) or open a minimal VJ
  // picker modal (multi variant). Fully works even when Horizon's own modal
  // fails to render for whatever reason.

  const productCache = new Map();

  // Resolve a /products/<handle> path to the current locale's handle. FR product
  // handles are translated (registered via translationsRegister), and Shopify's
  // .js endpoint does NOT follow translated handles — /fr-ca/products/<en-handle>.js
  // is a hard 404. The HTML route DOES 301 to the localized handle, so fetch it,
  // discard the body, and read the redirect target off response.url.
  async function resolveLocalizedProductPath(path) {
    try {
      const res = await _originalFetch(path);
      if (!res.ok) return null;
      if (res.body && typeof res.body.cancel === 'function') res.body.cancel().catch(() => {});
      const m = new URL(res.url, window.location.origin).pathname.match(/^(.*?\/products\/[^/?#]+)/);
      return m ? m[1] : null;
    } catch (_e) {
      return null;
    }
  }

  async function fetchProduct(productUrl) {
    if (!productUrl) return null;
    const url = new URL(productUrl, window.location.origin);
    // Keep the path up to and including the product handle, *including* any
    // Shopify Markets locale prefix (/fr-ca/, /en-ca/, …). Dropping it makes the
    // .js fetch resolve in the primary locale — where a locale-translated handle
    // 404s, so quickAdd() falls back to navigating to the PDP. That's the
    // "Ajout rapide opens the product page in French" bug.
    const pathMatch = url.pathname.match(/^(.*?\/products\/[^/?#]+)/);
    if (!pathMatch) return null;
    const path = pathMatch[1];
    if (productCache.has(path)) return productCache.get(path);
    try {
      // Fetch product JSON + siblings view in parallel. Siblings view is the
      // only way to read product metafields client-side without a Storefront
      // API token (Shopify's product.js omits metafields). Siblings is best-
      // effort: a 404 / parse failure just means no Product Type switcher.
      // NOTE: the siblings fetch deliberately omits `Accept: application/json`.
      // With that header Shopify content-negotiates the product URL and returns
      // the standard product.js representation, *ignoring* the ?view= override.
      // Our template still emits raw JSON; Response.json() parses it fine
      // regardless of Content-Type.
      let [prodRes, sibRes] = await Promise.all([
        _originalFetch(`${path}.js`, { headers: { Accept: 'application/json' } }),
        _originalFetch(`${path}?view=siblings`).catch(() => null),
      ]);
      if (!prodRes.ok) {
        // Likely a locale-mismatched handle (e.g. a wishlist entry saved on the
        // EN site, opened under /fr-ca — or the mirror case). Resolve the
        // localized handle via the HTML route's redirect and retry once.
        const resolved = await resolveLocalizedProductPath(path);
        if (!resolved || resolved === path) return null;
        [prodRes, sibRes] = await Promise.all([
          _originalFetch(`${resolved}.js`, { headers: { Accept: 'application/json' } }),
          _originalFetch(`${resolved}?view=siblings`).catch(() => null),
        ]);
      }
      if (!prodRes.ok) return null;
      const data = await prodRes.json();
      data._vj_siblings = null;
      if (sibRes && sibRes.ok) {
        try {
          const sib = await sibRes.json();
          if (sib && Array.isArray(sib.siblings)) data._vj_siblings = sib;
        } catch (_e) { /* template failure → no switcher, modal still works */ }
      }
      productCache.set(path, data);
      return data;
    } catch (_e) {
      return null;
    }
  }

  function findProductCardLink(el) {
    const card = el.closest('product-card, [data-product-card], .product-card, .card-wrapper');
    if (!card) return null;
    const a = card.querySelector('a[ref="productCardLink"], a.product-card__link, a[href*="/products/"]');
    return a && a.href ? a.href : null;
  }

  function isQuickAddButton(el) {
    if (!(el instanceof Element)) return false;
    // Horizon's "choose variant" button (multi-variant) on collection cards, plus
    // our own homepage quick-add buttons which opt in with [data-vj-quick-add] and
    // carry the product URL on data-product-url. (Horizon's single-variant quick-add
    // is a real form submit and is left to product-form-component.)
    return !!el.closest(
      '.quick-add__button--choose, button[on\\:click*="quick-add-component"], [data-vj-quick-add]'
    );
  }

  // Neuter Horizon's declarative on:click on multi-variant quick-add buttons so
  // its broken native modal never opens. <dialog>.showModal() uses the browser
  // top layer, which would paint *above* our custom modal. Stripping the
  // attribute prevents Horizon's delegated handler from matching.
  function disableHorizonQuickAdd(root) {
    const scope = root instanceof Element || root instanceof Document ? root : document;
    const sel = '.quick-add__button--choose, button[on\\:click*="quick-add-component"]';
    scope.querySelectorAll ? scope.querySelectorAll(sel).forEach(stripAttr) : null;
    if (scope instanceof Element && scope.matches && scope.matches(sel)) stripAttr(scope);
  }
  function stripAttr(btn) {
    if (btn.hasAttribute('on:click')) {
      btn.setAttribute('data-vj-horizon-onclick', btn.getAttribute('on:click'));
      btn.removeAttribute('on:click');
    }
  }

  function installDomWatcher() {
    disableHorizonQuickAdd(document);
    const obs = new MutationObserver((muts) => {
      for (const m of muts) {
        m.addedNodes.forEach(n => {
          if (n instanceof Element) disableHorizonQuickAdd(n);
        });
      }
    });
    obs.observe(document.documentElement, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installDomWatcher, { once: true });
  } else {
    installDomWatcher();
  }

  let vjModalEl = null;
  function ensureModal() {
    if (vjModalEl && document.body.contains(vjModalEl)) return vjModalEl;
    vjModalEl = document.createElement('div');
    vjModalEl.className = 'vj-qa-modal';
    vjModalEl.innerHTML = [
      '<div class="vj-qa-backdrop"></div>',
      '<div class="vj-qa-dialog" role="dialog" aria-modal="true">',
      '  <button type="button" class="vj-qa-close" aria-label="' + qaT('close') + '">&times;</button>',
      '  <div class="vj-qa-body"></div>',
      '</div>',
    ].join('');
    document.body.appendChild(vjModalEl);
    vjModalEl.querySelector('.vj-qa-backdrop').addEventListener('click', closeModal);
    vjModalEl.querySelector('.vj-qa-close').addEventListener('click', closeModal);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeModal(); });
    return vjModalEl;
  }

  function openModal(html) {
    const m = ensureModal();
    m.querySelector('.vj-qa-body').innerHTML = html;
    m.classList.add('is-open');
    document.body.classList.add('vj-qa-open');
  }

  function closeModal() {
    if (!vjModalEl) return;
    vjModalEl.classList.remove('is-open');
    document.body.classList.remove('vj-qa-open');
  }

  // Known-color hex map for common fabric colors so we can render a real
  // color chip when no swatch image is available.
  const COLOR_HEX = {
    'white': '#ffffff', 'natural': '#e8dfc8',
    'black': '#0a0a0a', 'heather black': '#1e1e1e',
    'dark heather': '#3a3a3a', 'charcoal': '#36454f', 'charcoal heather': '#3c3c3c',
    'gunmetal': '#3b3c44', 'gunmetal heather': '#4a4b52',
    'sport grey': '#a7a9ac', 'grey': '#808080', 'heather grey': '#b0b0b0', 'ash': '#c0c0c0',
    'silver': '#c0c0c0', 'light grey': '#d4d4d4',
    'red': '#b1212f', 'cardinal': '#c23b22', 'cherry red': '#bf1e2e',
    'maroon': '#800000', 'burgundy': '#800020',
    'orange': '#f58220', 'burnt orange': '#cc5500', 'mustard': '#c89b19',
    'yellow': '#f4c518', 'gold': '#d4af37', 'daisy': '#fbe870',
    'kelly green': '#3f9b0b', 'green': '#2e7d32', 'forest green': '#228b22', 'military green': '#4b5320', 'army': '#4b5320', 'olive': '#708238',
    'mint': '#98d8a8', 'heather mint': '#a8d8b8',
    'light blue': '#87ceeb', 'sky': '#87ceeb', 'carolina blue': '#56a0d3',
    'royal': '#4169e1', 'royal blue': '#4169e1', 'blue': '#1e3a8a',
    'navy': '#0a1a3e', 'classic navy': '#0a1a3e',
    'storm blue': '#34647d', 'indigo': '#4b0082',
    'purple': '#6a1b9a', 'team purple': '#5b2a86',
    'pink': '#ff7fb0', 'light pink': '#ffc0cb', 'heather pink': '#ffb6c1', 'hot pink': '#ff1493',
    'brown': '#5c4033', 'chocolate': '#3c2414', 'tan': '#d2b48c', 'sand': '#c2b280',
  };

  function colorHex(name) {
    const k = String(name || '').trim().toLowerCase();
    return COLOR_HEX[k] || null;
  }

  function variantFeaturedImageSrc(variant) {
    if (!variant) return null;
    const f = variant.featured_image;
    if (!f) return null;
    if (typeof f === 'string') return f;
    if (typeof f === 'object' && f.src) return f.src;
    return null;
  }

  function renderPicker(product, productUrl) {
    const money = (cents) => '$' + (cents / 100).toFixed(2);
    const escapeHtml = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    // Render current + optional strikethrough compare-at when discounted.
    // Mirrors PDP .vj-price-compare convention.
    const priceHtml = (curCents, cmpCents) => {
      const now = `<span class="vj-qa-price-now">${money(curCents)}</span>`;
      const isDiscount = Number.isFinite(cmpCents) && cmpCents > curCents;
      const cmp = isDiscount ? ` <span class="vj-qa-price-compare">${money(cmpCents)}</span>` : '';
      return now + cmp;
    };

    const featured = product.featured_image && typeof product.featured_image === 'object'
      ? product.featured_image.src : product.featured_image;
    // Pale-text card art (2026-06-30 system): the modal's at-rest image mirrors
    // the grid card — pale-recoloured caption art when the design has one. The
    // URL rides in on the ?view=siblings JSON (product.js omits metafields).
    // Picking a color still swaps to that color's mockup; deselecting falls
    // back to data-default-src, so pale stays the resting state.
    // Light mat (mig 101, wired 2026-08-14): mat designs paint the cream field
    // behind the raw art (img.vj-card--light-mat in vj-base.css). THE MAT WINS
    // over pale — pale on cream is invisible. Mockup swaps are opaque, so the
    // static class is harmless while a colour is selected.
    const matOn = !!(product._vj_siblings && product._vj_siblings.card_light_mat);
    const paleArt = (!matOn && product._vj_siblings && product._vj_siblings.card_pale_art) || null;
    const atRestSrc = paleArt || featured;
    const img = atRestSrc
      ? `<img src="${escapeHtml(atRestSrc)}" alt="${escapeHtml(product.title)}" class="vj-qa-img${matOn ? ' vj-card--light-mat' : ''}" data-default-src="${escapeHtml(atRestSrc)}"/>`
      : '';

    const rawOptions = Array.isArray(product.options) ? product.options : [];
    const optionNames = rawOptions.map(o => (o && typeof o === 'object') ? (o.name || '') : String(o || ''));
    const colorOptionIdx = optionNames.findIndex(n => /colou?r/i.test(n));
    const sizeOptionIdx  = optionNames.findIndex(n => /size/i.test(n));

    // Canonical apparel-size ordering. Shopify stores these products' size option
    // values alphabetically (2XL, 3XL, L, M, S, XL), so sort the size axis to read
    // the way humans expect. Mirrors SIZES_ORDER in sections/vj-product.liquid.
    const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];
    const sizeRank = (v) => {
      const idx = SIZE_ORDER.indexOf(String(v).trim().toUpperCase());
      return idx === -1 ? SIZE_ORDER.length : idx;
    };

    const state = {};
    optionNames.forEach((_n, i) => { state[i] = null; });

    function optionValues(i) {
      const set = new Set();
      product.variants.forEach(v => {
        let val = null;
        if (Array.isArray(v.options)) val = v.options[i];
        if (val == null) val = v['option' + (i + 1)];
        if (val != null) set.add(val);
      });
      let arr = Array.from(set);
      // Region gate: colors not stocked in the visitor's region get no
      // swatch (same rule as the PDP swatch loop).
      if (i === colorOptionIdx && regionFilterOn) {
        arr = arr.filter(colorOkInRegion);
      }
      if (i === sizeOptionIdx) {
        arr.sort((a, b) => {
          const ra = sizeRank(a), rb = sizeRank(b);
          return ra !== rb ? ra - rb : String(a).localeCompare(String(b));
        });
      }
      return arr;
    }

    function variantOptionVal(v, i) {
      if (Array.isArray(v.options) && v.options[i] != null) return v.options[i];
      return v['option' + (i + 1)];
    }

    // -- Region gate (2026-08-31) -----------------------------------------
    // Mirror of the PDP's region-aware filtering (sections/vj-product.liquid).
    // Shopify's product.js omits metafields, so the ?view=siblings JSON
    // (templates/product.siblings.liquid) now carries the resolved visitor
    // region plus this product's region_available_colors / _sizes payloads.
    // Fail-open when the siblings fetch failed or the product has no region
    // data (legacy publish) -- matches the PDP's rollout-safe default.
    // Fail-CLOSED when region data exists and this region's color list is
    // empty: bail to the PDP, which renders the "not available in your
    // region" strip. Never reintroduce fail-open there (2026-06-22 lesson).
    const sibMeta = product._vj_siblings || null;
    const regionFilterOn = !!(sibMeta && sibMeta.region_filter_on);
    const allowedColors = regionFilterOn && Array.isArray(sibMeta.allowed_colors)
      ? sibMeta.allowed_colors : null;
    const regionSizeMap = regionFilterOn && sibMeta.region_sizes && typeof sibMeta.region_sizes === 'object'
      ? sibMeta.region_sizes : null;

    function colorOkInRegion(color) {
      if (!allowedColors) return true;
      return allowedColors.indexOf(color) !== -1;
    }
    // Rollout-safe like the PDP's isSizeAvailableInRegion: a color with no
    // entry (or an empty list) in the size map keeps all its sizes.
    function sizeOkInRegion(color, size) {
      if (!regionSizeMap || color == null) return true;
      const sizes = regionSizeMap[color];
      if (!Array.isArray(sizes) || sizes.length === 0) return true;
      return sizes.indexOf(size) !== -1;
    }
    function variantOkInRegion(v) {
      if (!regionFilterOn || colorOptionIdx < 0) return true;
      const c = variantOptionVal(v, colorOptionIdx);
      if (!colorOkInRegion(c)) return false;
      if (sizeOptionIdx >= 0) return sizeOkInRegion(c, variantOptionVal(v, sizeOptionIdx));
      return true;
    }

    if (colorOptionIdx >= 0 && regionFilterOn && optionValues(colorOptionIdx).length === 0) {
      // Every color is region-blocked -> fail-closed: the PDP explains it.
      closeModal();
      window.location.href = productUrl;
      return;
    }

    function variantForState() {
      return product.variants.find(v => {
        for (let i = 0; i < optionNames.length; i++) {
          if (state[i] !== null && variantOptionVal(v, i) !== state[i]) return false;
        }
        return true;
      });
    }

    // For each color value, find the first variant with that color (for its image).
    const colorImageByValue = {};
    if (colorOptionIdx >= 0) {
      product.variants.forEach(v => {
        const c = variantOptionVal(v, colorOptionIdx);
        if (c == null || colorImageByValue[c]) return;
        const src = variantFeaturedImageSrc(v);
        if (src) colorImageByValue[c] = src;
      });
    }

    function swatchHtml(optionIdx, value) {
      const isColor = optionIdx === colorOptionIdx;
      if (isColor) {
        const hex = colorHex(value);
        const mockup = colorImageByValue[value];
        const bgStyle = mockup
          ? `background-image:url('${escapeHtml(mockup)}');background-size:cover;background-position:center;`
          : hex
            ? `background-color:${hex};`
            : 'background-color:#555;';
        return [
          `<button type="button" class="vj-qa-swatch vj-qa-swatch--color" data-value="${escapeHtml(value)}" aria-label="${escapeHtml(vjLocalizeColor(value))}" title="${escapeHtml(vjLocalizeColor(value))}">`,
          `  <span class="vj-qa-swatch-chip" style="${bgStyle}"></span>`,
          '</button>',
        ].join('');
      }
      return `<button type="button" class="vj-qa-swatch" data-value="${escapeHtml(value)}">${escapeHtml(value)}</button>`;
    }

    const optionsHtml = optionNames.map((name, i) => {
      const vals = optionValues(i);
      const isColor = i === colorOptionIdx;
      return [
        '<div class="vj-qa-option">',
        `  <div class="vj-qa-option-label">${escapeHtml(qaOptLabel(name))}${isColor ? ' <span class="vj-qa-option-selected" data-color-label></span>' : ''}</div>`,
        `  <div class="vj-qa-option-values${isColor ? ' vj-qa-option-values--colors' : ''}" data-option-index="${i}">`,
        vals.map(v => swatchHtml(i, v)).join(''),
        '  </div>',
        '</div>',
      ].join('');
    }).join('');

    // ── Product Type selector ─────────────────────────────────────────────
    // Renders ABOVE the variant options when the product has 2+ published
    // siblings (different garments carrying the same artwork). Native <select>
    // mirrors the PDP — opens the system picker on mobile. On change we re-
    // enter quickAdd() with the chosen sibling's URL; the modal re-renders in
    // place with that sibling's variants. No close/reopen flicker.
    let productTypeHtml = '';
    const sibData = product && product._vj_siblings;
    let vjQaSiblings = (sibData && Array.isArray(sibData.siblings)) ? sibData.siblings : [];
    const vjQaCurrentKey = sibData ? (sibData.current_garment_key || '') : '';
    // Region-blocked siblings (e.g. phone cases for CA visitors) are hidden,
    // same as the PDP switcher. vjFilterSiblingsByRegion keeps the current
    // sibling and fail-opens on legacy entries without a regions field.
    if (typeof window.vjFilterSiblingsByRegion === 'function') {
      vjQaSiblings = window.vjFilterSiblingsByRegion(
        vjQaSiblings, vjQaCurrentKey, (sibMeta && sibMeta.region) || '', regionFilterOn);
    }
    if (vjQaSiblings.length >= 2
        && typeof window.vjSynthesizeSiblingLabel === 'function'
        && typeof window.vjBuildSiblingUrl === 'function') {
      const currentKey = vjQaCurrentKey;
      const options = vjQaSiblings.map(s => {
        const url = window.vjBuildSiblingUrl(s);
        const label = window.vjSynthesizeSiblingLabel(s) || s.garment_key || '';
        const selected = s.garment_key === currentKey ? ' selected' : '';
        return `<option value="${escapeHtml(url)}" data-garment-key="${escapeHtml(s.garment_key)}"${selected}>${escapeHtml(label)}</option>`;
      }).join('');
      productTypeHtml = [
        '<div class="vj-qa-option vj-qa-product-type">',
        '  <div class="vj-qa-option-label">' + qaT('product_type') + '</div>',
        '  <select class="vj-qa-product-type__select" data-vj-qa-product-type>',
        options,
        '  </select>',
        '</div>',
      ].join('');
    }

    const html = [
      '<div class="vj-qa-product">',
      `  <div class="vj-qa-media">${img}</div>`,
      '  <div class="vj-qa-details">',
      `    <h3 class="vj-qa-title">${escapeHtml(product.title)}</h3>`,
      `    <div class="vj-qa-price" data-price>${priceHtml(product.price, product.compare_at_price)}</div>`,
      `    ${productTypeHtml}`,
      `    ${optionsHtml}`,
      '    <button type="button" class="vj-qa-add" disabled>' + qaT('select_options') + '</button>',
      '    <div class="vj-qa-msg" role="status"></div>',
      `    <a class="vj-qa-more" href="${escapeHtml(productUrl || '#')}">${qaT('see_other')}</a>`,
      '  </div>',
      '</div>',
    ].join('');

    openModal(html);

    const modal = vjModalEl;
    const priceEl = modal.querySelector('[data-price]');
    const addBtn  = modal.querySelector('.vj-qa-add');
    const msgEl   = modal.querySelector('.vj-qa-msg');
    const imgEl   = modal.querySelector('.vj-qa-img');
    const colorLabelEl = modal.querySelector('[data-color-label]');

    function refreshUI() {
      optionNames.forEach((_n, i) => {
        const group = modal.querySelector(`.vj-qa-option-values[data-option-index="${i}"]`);
        if (!group) return;
        group.querySelectorAll('.vj-qa-swatch').forEach(b => {
          const v = b.dataset.value;
          const stateClone = { ...state, [i]: v };
          const matches = product.variants.some(variant => {
            if (!variant.available) return false;
            if (!variantOkInRegion(variant)) return false;
            for (let j = 0; j < optionNames.length; j++) {
              if (stateClone[j] !== null && variantOptionVal(variant, j) !== stateClone[j]) return false;
            }
            return true;
          });
          b.classList.toggle('is-selected', state[i] === v);
          b.classList.toggle('is-unavailable', !matches);
        });
      });

      if (colorLabelEl) {
        colorLabelEl.textContent = state[colorOptionIdx] ? `— ${vjLocalizeColor(state[colorOptionIdx])}` : '';
      }

      // Swap main image to the selected color's mockup.
      if (imgEl && colorOptionIdx >= 0 && state[colorOptionIdx]) {
        const mockup = colorImageByValue[state[colorOptionIdx]];
        if (mockup) imgEl.src = mockup;
      } else if (imgEl && imgEl.dataset.defaultSrc) {
        imgEl.src = imgEl.dataset.defaultSrc;
      }

      const variant = variantForState();
      const allChosen = optionNames.every((_n, i) => state[i] !== null);
      if (variant && variant.available) {
        priceEl.innerHTML = priceHtml(variant.price, variant.compare_at_price);
      }
      if (allChosen && variant && variant.available && variantOkInRegion(variant)) {
        addBtn.disabled = false;
        addBtn.textContent = qaT('add');
        addBtn.dataset.variantId = variant.id;
      } else if (allChosen && variant && variant.available) {
        // In stock globally but not fulfilled in the visitor's region
        // (per-size region gate).
        addBtn.disabled = true;
        addBtn.textContent = qaT('not_in_region');
      } else if (allChosen && variant && !variant.available) {
        addBtn.disabled = true;
        addBtn.textContent = qaT('sold_out');
      } else {
        // Name the missing field like the PDP does, instead of the generic
        // "Choisir les options" (QA 2026-07 MOBILE R5-2).
        addBtn.disabled = true;
        const needColor = colorOptionIdx >= 0 && state[colorOptionIdx] === null;
        const needSize  = sizeOptionIdx  >= 0 && state[sizeOptionIdx]  === null;
        if (needColor && needSize) addBtn.textContent = qaT('select_color_size');
        else if (needColor)        addBtn.textContent = qaT('select_color');
        else if (needSize)         addBtn.textContent = qaT('select_size');
        else                       addBtn.textContent = qaT('select_options');
      }
    }

    modal.querySelectorAll('.vj-qa-swatch').forEach(btn => {
      btn.addEventListener('click', () => {
        const group = btn.parentElement;
        const i = Number(group.dataset.optionIndex);
        state[i] = btn.dataset.value;
        refreshUI();
      });
    });

    const ptSelect = modal.querySelector('[data-vj-qa-product-type]');
    if (ptSelect) {
      ptSelect.addEventListener('change', (e) => {
        const url = e.target.value;
        if (!url) return;
        // Re-entrant: quickAdd swaps the modal body in place via openModal().
        quickAdd(url);
      });
    }

    optionNames.forEach((_n, i) => {
      const vals = optionValues(i);
      if (vals.length === 1) state[i] = vals[0];
    });

    addBtn.addEventListener('click', async () => {
      const variantId = addBtn.dataset.variantId;
      if (!variantId) return;
      addBtn.disabled = true;
      addBtn.textContent = qaT('adding');
      try {
        await add(variantId, 1);
        msgEl.textContent = qaT('added');
        setTimeout(closeModal, 400);
      } catch (err) {
        msgEl.textContent = (err && err.message) || qaT('add_error');
        addBtn.disabled = false;
        addBtn.textContent = qaT('add');
      }
    });

    refreshUI();
  }

  // Open the VJ quick-add picker for a product URL. Single-variant products are
  // added straight to cart; multi-variant ones get the swatch/size picker modal.
  // Falls back to navigating to the PDP if anything goes wrong. Exposed on
  // window.VJCart so non-Horizon surfaces (homepage bands, etc.) can reuse the
  // exact same modal collection cards use.
  async function quickAdd(productUrl, btn) {
    if (!productUrl) return;

    if (btn) btn.setAttribute('data-vj-loading', '1');
    openModal('<div class="vj-qa-loading">' + qaT('loading') + '</div>');

    const product = await fetchProduct(productUrl);
    if (btn) btn.removeAttribute('data-vj-loading');

    if (!product) {
      closeModal();
      window.location.href = productUrl;
      return;
    }

    if (product.variants.length === 1) {
      try {
        await add(product.variants[0].id, 1);
        closeModal();
      } catch (_e) {
        closeModal();
        window.location.href = productUrl;
      }
      return;
    }

    try {
      renderPicker(product, productUrl);
    } catch (err) {
      console.error('[VJCart] renderPicker failed:', err);
      closeModal();
      window.location.href = productUrl;
    }
  }

  async function handleQuickAddClick(e) {
    const target = e.target;
    if (!(target instanceof Element)) return;
    if (!isQuickAddButton(target)) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();

    const btn = target.closest('button, [data-vj-quick-add]');
    const productUrl = (btn && btn.dataset && btn.dataset.productUrl) || findProductCardLink(target);
    quickAdd(productUrl, btn instanceof HTMLElement ? btn : null);
  }

  window.VJCart.quickAdd = quickAdd;

  // Capture-phase so we win against Horizon's own delegated click listener.
  document.addEventListener('click', handleQuickAddClick, { capture: true });

  // ── Free-shipping progress bar ──────────────────────────────────────────────
  // Renders a small progress bar at the top of the cart drawer showing
  // "$X more for free shipping" and animating to "you qualify" when the
  // subtotal crosses the threshold. Thresholds are per-currency; CAD is the
  // canonical free-shipping floor ($75 CAD). A Liquid-emitted
  // `window.VJ_SHIPPING_THRESHOLDS` (snippets/vj-shipping-config.liquid, wired
  // off the theme setting "Free shipping threshold (CAD, in cents)") overrides
  // these defaults so the number has a single source of truth. Listens on
  // cart:update so it stays in sync with Horizon's cart morphing.

  const SHIPPING_THRESHOLDS = Object.assign({
    USD: 5500,   // $55.00
    CAD: 7500,   // $75.00 — canonical free-shipping floor
    EUR: 5000,
    GBP: 4000,
    AUD: 7500,
  }, (typeof window !== 'undefined' && window.VJ_SHIPPING_THRESHOLDS) || {});

  const SHIPPING_I18N = {
    en: {
      away: (amt) => `Add ${amt} more for free shipping`,
      qualified: 'You qualify for free shipping',
      // Frames & prints (wall art) always ship at their own rate — never free.
      wallArtNote: 'Frames & prints ship separately',
    },
    fr: {
      away: (amt) => `Ajoute ${amt} de plus pour la livraison gratuite`,
      qualified: 'Tu as la livraison gratuite',
      wallArtNote: 'Les cadres et affiches sont expédiés à part',
    },
  };

  function vjLocale() {
    return (document.documentElement.lang || 'en').toLowerCase().split('-')[0];
  }

  // Quick-add ("product preview") modal strings. The modal is built in JS, so
  // its chrome (labels + button states) can't use theme locale keys — translate
  // here. Option names come from Shopify in English (Color/Size); qaOptLabel
  // maps them for display only.
  var QA_STRINGS = {
    en: { product_type: 'Product type', select_options: 'Select options', see_other: 'See other options →',
      add: 'Add to cart', sold_out: 'Sold out', adding: 'Adding…', added: 'Added to cart',
      add_error: 'Could not add to cart', loading: 'Loading…', close: 'Close', color: 'Color', size: 'Size',
      select_color: 'Select a color', select_size: 'Select a size', select_color_size: 'Select a color & size',
      not_in_region: 'Not available in your region' },
    fr: { product_type: 'Type de produit', select_options: 'Choisir les options', see_other: 'Voir les autres options →',
      add: 'Ajouter au panier', sold_out: 'Épuisé', adding: 'Ajout…', added: 'Ajouté au panier',
      add_error: 'Impossible d’ajouter au panier', loading: 'Chargement…', close: 'Fermer', color: 'Couleur', size: 'Taille',
      select_color: 'Choisir une couleur', select_size: 'Choisir une taille', select_color_size: 'Choisir une couleur et une taille',
      not_in_region: 'Non disponible dans ta région' },
  };
  function qaT(k) {
    var l = vjLocale() === 'fr' ? 'fr' : 'en';
    return QA_STRINGS[l][k] || QA_STRINGS.en[k] || k;
  }
  function qaOptLabel(name) {
    var l = String(name || '').trim().toLowerCase();
    if (l === 'color' || l === 'colour') return qaT('color');
    if (l === 'size') return qaT('size');
    return name;
  }

  // Color-name FR display map — mirrors localizeColor() in vj-product.liquid.
  // DISPLAY-ONLY: the option VALUE stays canonical English so variant matching
  // (quick-add) and facet filtering (collection Color filter) keep working; only
  // the shown label is localized. Obscure Printful names fall back to English.
  var VJ_COLOR_FR = {
    'black': 'Noir', 'white': 'Blanc', 'navy': 'Marine', 'heather navy': 'Marine chiné',
    'red': 'Rouge', 'heather red': 'Rouge chiné', 'cardinal': 'Cardinal', 'maroon': 'Bordeaux',
    'royal': 'Bleu royal', 'true royal': 'Bleu royal', 'heather true royal': 'Bleu royal chiné',
    'heather deep royal': 'Bleu royal foncé chiné', 'sport grey': 'Gris sport',
    'athletic heather': 'Gris chiné', 'dark heather': 'Gris foncé chiné',
    'dark grey': 'Gris foncé', 'dark grey heather': 'Gris foncé chiné', 'charcoal': 'Anthracite',
    'ash': 'Cendre', 'graphite heather': 'Graphite chiné', 'ice grey': 'Gris glace',
    'forest': 'Vert forêt', 'forest green': 'Vert forêt', 'heather forest': 'Vert forêt chiné',
    'military green': 'Vert militaire', 'army': 'Vert armée', 'olive': 'Olive', 'leaf': 'Vert feuille',
    'irish green': 'Vert irlandais', 'heather irish green': 'Vert irlandais chiné', 'kelly': 'Vert kelly',
    'sage': 'Sauge', 'gold': 'Or', 'yellow': 'Jaune', 'mustard': 'Moutarde', 'daisy': 'Jaune marguerite',
    'orange': 'Orange', 'burnt orange': 'Orange brûlé', 'purple': 'Mauve', 'team purple': 'Mauve',
    'light violet': 'Violet pâle', 'berry': 'Baie', 'pink': 'Rose', 'light pink': 'Rose pâle',
    'azalea': 'Azalée', 'light blue': 'Bleu pâle', 'baby blue': 'Bleu bébé', 'carolina blue': 'Bleu Carolina',
    'heather columbia blue': 'Bleu Columbia chiné', 'sky': 'Ciel', 'ocean blue': 'Bleu océan',
    'indigo blue': 'Bleu indigo', 'teal': 'Sarcelle', 'aqua': 'Aqua', 'sand': 'Sable', 'natural': 'Naturel',
    'tan': 'Beige', 'soft cream': 'Crème', 'dark chocolate': 'Chocolat foncé', 'brown savana': 'Brun savane',
    'vintage black': 'Noir vintage', 'vintage white': 'Blanc vintage', 'charcoal grey': 'Gris anthracite',
    'heather royal': 'Bleu royal chiné',
  };
  function vjLocalizeColor(name) {
    if (vjLocale() !== 'fr' || !name) return name;
    return VJ_COLOR_FR[String(name).toLowerCase().trim()] || name;
  }
  window.vjLocalizeColor = vjLocalizeColor;

  // FR labels for the metafield facets (Style / Ambiance / Sous-catégorie).
  // Generated from vj.tags.* + tone map + sub-collection titles (see
  // shopify/gen-facet-fr-map.js). Keys are NORMALIZED (lowercase, _ and - → space)
  // so the lookup matches whether the facet renders the raw slug ("comic_book_ink")
  // or a formatted label ("Comic book ink"). Display-only — the filter value is
  // untouched.
  var VJ_FACET_FR = {
    '2000s blockbusters': "Blockbusters des années 2000",
    '80s and 90s cinema': "Cinéma des années 80 et 90",
    'absurd': "Absurde",
    'action adventure': "Action-aventure",
    'action and adventure': "Action et aventure",
    'anime and manga': "Anime et manga",
    'anime cel': "Cel anime",
    'art deco': "Art déco",
    'battle royale': "Battle royale",
    'blackwork linocut': "Linogravure noire",
    'bold': "Audacieux",
    'chibi doodle sketch': "Croquis chibi",
    'chibi kawaii': "Chibi / Kawaii",
    'classic hollywood': "Hollywood classique",
    'comedy': "Comédie",
    'comic book ink': "Encre BD",
    'cult classics': "Classiques cultes",
    'cute': "Mignon",
    'cyberpunk': "Cyberpunk",
    'dark': "Sombre",
    'deadpan': "Pince-sans-rire",
    'editorial cartoon': "Caricature éditoriale",
    'epic': "Épique",
    'fantasy': "Fantastique",
    'fighting': "Jeux de combat",
    'flat vector': "Vecteur plat",
    'fps and shooter': "FPS et jeux de tir",
    'franchise crossovers': "Crossovers de franchises",
    'funny': "Drôle",
    'gig poster': "Affiche de concert",
    'halftone pop art': "Pop art tramé",
    'hd and modern era': "Ère HD et moderne",
    'horror': "Horreur",
    'horror games': "Jeux d’horreur",
    'iconic villains': "Méchants iconiques",
    'indie and pixel art': "Indé et pixel art",
    'internet culture': "Culture internet",
    'low poly': "Low poly",
    'manga line': "Ligne manga",
    'minimalist silhouette': "Silhouette minimaliste",
    'moba': "MOBA",
    'movie quotes': "Répliques cultes",
    'musicals': "Comédies musicales",
    'naive doodle': "Gribouillage naïf",
    'nes and snes era': "Ère NES et SNES",
    'nostalgic': "Nostalgique",
    'open world': "Monde ouvert",
    'pencil sketch': "Croquis au crayon",
    'pixel art': "Pixel art",
    'platformer': "Jeux de plateforme",
    'politics': "Politique",
    'propaganda screenprint': "Propagande",
    'ps1 and n64 era': "Ère PS1 et N64",
    'ps2 and gamecube era': "Ère PS2 et GameCube",
    'reality tv': "Téléréalité",
    'retro and arcade': "Rétro et arcade",
    'risograph': "Risographie",
    'rock': "Rock",
    'roguelike and soulslike': "Roguelike et soulslike",
    'rpg and jrpg': "RPG et JRPG",
    'sci fi': "Science-fiction",
    'screen print punk': "Sérigraphie punk",
    'sticker style': "Autocollant",
    'streaming era tv': "Télé de l’ère streaming",
    'street art': "Street art",
    'superhero mcu and dc': "Superhéros (MCU et DC)",
    'thriller and crime': "Thriller et crime",
    'traditional tattoo': "Tatouage old-school",
    'ukiyo e': "Ukiyo-e",
    'vintage travel poster': "Affiche de voyage rétro",
    'watercolor': "Aquarelle",
    'westerns': "Westerns",
    'woodcut print': "Gravure sur bois",
  };
  function vjFacetLabelFr(raw) {
    var lc = String(raw || '').toLowerCase().trim();
    if (VJ_COLOR_FR[lc]) return VJ_COLOR_FR[lc];
    var n = lc.replace(/[_-]+/g, ' ');
    return VJ_FACET_FR[n] || null;
  }

  // Collection Color-filter facet: the checkbox labels are the English option
  // values. Localize the DISPLAYED text only (the checkbox value/name — what the
  // filter query uses — is untouched). Idempotent (FR labels don't match a map
  // key), so it's safe to re-run after Shopify's faceted-search AJAX re-render.
  function vjTranslateColorFacets() {
    if (vjLocale() !== 'fr') return;
    var els = document.querySelectorAll('.checkbox__label-text, .facets__swatch-label, .facets__pill-label');
    for (var i = 0; i < els.length; i++) {
      var raw = (els[i].textContent || '').trim();
      var fr = vjFacetLabelFr(raw);
      if (fr && fr !== raw) els[i].textContent = fr;
    }
  }
  function vjSetupColorFacetTranslation() {
    if (vjLocale() !== 'fr') return;
    vjTranslateColorFacets();
    var target = document.querySelector('.facets, facet-filters-form, [id^="Facet"]') || document.querySelector('main');
    if (target && window.MutationObserver) {
      var scheduled = false;
      new MutationObserver(function () {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(function () { scheduled = false; vjTranslateColorFacets(); });
      }).observe(target, { childList: true, subtree: true });
    }
  }

  function vjFormatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  // Cache of the last authoritative cart so the self-heal observer below can
  // re-inject the band synchronously (no fetch) the instant a Horizon drawer
  // morph drops it.
  let _vjLastCart = null;

  async function vjFetchCart() {
    try {
      const r = await _originalFetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!r.ok) return null;
      _vjLastCart = await r.json();
      return _vjLastCart;
    } catch { return null; }
  }

  function vjFindCartDrawer() {
    return document.querySelector(
      'cart-drawer-component, cart-drawer, [data-cart-drawer], #CartDrawer, #cart-drawer'
    );
  }

  function vjRenderShippingBar(cart) {
    if (cart) _vjLastCart = cart;
    cart = cart || _vjLastCart;
    const drawer = vjFindCartDrawer();
    if (!drawer || !cart) return;

    const currency  = cart.currency || 'USD';
    const threshold = SHIPPING_THRESHOLDS[currency];
    if (!threshold) return; // unknown currency — skip rather than guess

    // Wall art (frames & prints) NEVER qualifies for free shipping — the
    // customer always pays its shipping (owner rule 2026-07-08; heavy/fragile
    // framed items + region-specific fulfilment make blanket free shipping a
    // margin sink). Shopify enforces this via a separate wall-art shipping
    // profile excluded from the free-over-threshold rate; here we keep the
    // progress bar HONEST by not counting wall-art line items toward it.
    // Safe no-op for the current catalog (no 'Wall Art' product_type in cart).
    const wallArtCents = (cart.items || [])
      .filter(it => it && it.product_type === 'Wall Art')
      .reduce((s, it) => s + (it.final_line_price || it.line_price || 0), 0);
    const hasWallArt = wallArtCents > 0;

    const total     = Math.max(0, (cart.total_price || 0) - wallArtCents); // free-ship-eligible only
    const remaining = Math.max(0, threshold - total);
    const percent   = Math.min(100, (total / threshold) * 100);
    const qualified = total >= threshold;
    const empty     = total === 0;

    const lang = vjLocale();
    const i18n = SHIPPING_I18N[lang] || SHIPPING_I18N.en;
    let message = empty
      ? i18n.away(vjFormatMoney(threshold, currency))
      : qualified
        ? i18n.qualified
        : i18n.away(vjFormatMoney(remaining, currency));
    if (hasWallArt) message += ` · ${i18n.wallArtNote}`;

    let bar = drawer.querySelector('#vj-shipping-bar');
    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'vj-shipping-bar';
      bar.className = 'vj-shipping-bar';
      bar.innerHTML = '<div class="vj-shipping-bar__msg"></div><div class="vj-shipping-bar__track"><div class="vj-shipping-bar__fill"></div></div>';
      // Inject INSIDE <cart-items-component> (Horizon's flex column: sticky
      // header + flex-1 scrollable content) so the content area shrinks to
      // absorb the bar's height. Injecting it as a sibling (into
      // .cart-drawer__inner) pushes the 100%-tall component past the dialog's
      // overflow:hidden edge — the bottom ~60px of the summary (last payment
      // button) gets clipped with no way to scroll to it.
      // Position: AFTER .cart-drawer__header, not before it — Horizon's close
      // (X) button is position:fixed at the dialog's top-right (base.css
      // .close-button), sized to line up with the header row. A bar above the
      // header pushes the header down and the X lands on top of the bar
      // (QA 2026-07-07, MISC row 4).
      // Fallback: the drawer inner, then the drawer itself (empty-cart drawers
      // are short, so clipping can't happen there).
      const items  = drawer.querySelector('cart-items-component');
      const host   = items || drawer.querySelector('.cart-drawer__inner') || drawer;
      const header = host.querySelector(':scope > .cart-drawer__header');
      if (header) header.insertAdjacentElement('afterend', bar);
      else host.insertBefore(bar, host.firstChild);
    }

    bar.querySelector('.vj-shipping-bar__msg').textContent = message;
    bar.querySelector('.vj-shipping-bar__fill').style.width = `${percent}%`;
    bar.classList.toggle('is-qualified', qualified);
    bar.classList.toggle('is-empty', empty);
  }

  let _vjShippingDebounce = null;
  function vjQueueShippingBarUpdate(cart) {
    clearTimeout(_vjShippingDebounce);
    _vjShippingDebounce = setTimeout(async () => {
      const c = cart || await vjFetchCart();
      vjRenderShippingBar(c);
      vjRenderCrossSell(c);
    }, 50);
  }

  // ── Cross-sell: "Complete the look" row in the drawer (2026-07-06 P1) ──────
  // The drawer is the primary checkout path (ATC auto-opens it) and had zero
  // recommendations. This row offers the in-cart design's OTHER garments
  // (hoodie/premium/classic siblings — same artwork, data from the same
  // ?view=siblings JSON the quick-add Product Type switcher uses). Tap →
  // VJCart.quickAdd, whose <dialog> stacks above the drawer's. Injected
  // INSIDE cart-items-component (appended) for the same clipping reason the
  // shipping bar documents above. Best-effort region filter via
  // Shopify.country → sibling.regions; unknown country degrades to no filter
  // (parity with collection-card quick-add). Pairs with the free-shipping
  // bar: the sibling is the natural "close the gap" add.
  const XS_I18N = {
    en: { title: 'Complete the look' },
    fr: { title: 'Complète le look' },
  };
  const XS_REGION_BY_COUNTRY = {
    US: 'usa', CA: 'canada', GB: 'uk', AU: 'australia', JP: 'japan', BR: 'brazil',
  };
  const XS_MAX_TILES = 3;
  // PARKED 2026-07-08 (owner): the cart "Complete the look" row is disabled
  // until Wall Art ships. It currently upsells GARMENT siblings, but the owner
  // wants the eventual cross-sell to be FRAMES only (see the frames-only
  // feedback note). Kept dormant — flip XS_ENABLED to true to bring it back.
  const XS_ENABLED = false;
  let _vjXsToken = 0;      // stale-render guard (async fetches vs fast cart edits)
  let _vjXsEmptyFor = null; // cart token we decided "no tiles" for — heal skips it

  // Local escaper — the quick-add modal's escapeHtml lives in a different
  // closure and is NOT visible here (learned the hard way: silent unhandled
  // rejection, no row).
  function vjXsEsc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function vjSiblingPath(handle) {
    if (typeof window.vjBuildSiblingUrl === 'function') return window.vjBuildSiblingUrl(handle);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}products/${handle}`;
  }

  function vjXsImg(prod) {
    const src = prod && (prod.featured_image || (prod.images && prod.images[0]));
    if (!src) return '';
    const sep = String(src).includes('?') ? '&' : '?';
    return `${src}${sep}width=160`;
  }

  async function vjRenderCrossSell(cart) {
    // Parked until Wall Art launch (see XS_ENABLED note above). Pull any row
    // that a cached render already injected, and mark this cart token as
    // "settled" so the self-heal doesn't keep re-invoking us on DOM mutations.
    if (!XS_ENABLED) {
      const stale = document.getElementById('vj-cross-sell');
      if (stale) stale.remove();
      _vjXsEmptyFor = cart ? cart.token : null;
      return;
    }
    const drawer = vjFindCartDrawer();
    if (!drawer) return;
    const existing = document.getElementById('vj-cross-sell');
    if (!cart || !Array.isArray(cart.items) || !cart.items.length) {
      _vjXsEmptyFor = cart ? cart.token : null;
      if (existing) existing.remove();
      return;
    }
    const token = ++_vjXsToken;

    const inCart = new Set(cart.items.map(i => i.handle));
    const country = (window.Shopify && window.Shopify.country) || '';
    const region = XS_REGION_BY_COUNTRY[country] || null;

    // Collect sibling candidates from the first two cart lines.
    const picks = [];
    const seen = new Set();
    for (const item of cart.items.slice(0, 2)) {
      if (!item.handle) continue;
      const prod = await fetchProduct(vjSiblingPath(item.handle));
      if (token !== _vjXsToken) return; // cart changed mid-fetch
      const sib = prod && prod._vj_siblings;
      if (!sib || !Array.isArray(sib.siblings)) continue;
      for (const s of sib.siblings) {
        if (picks.length >= XS_MAX_TILES) break;
        if (!s || !s.handle || inCart.has(s.handle) || seen.has(s.handle)) continue;
        if (region && Array.isArray(s.regions) && s.regions.length
            && s.regions.indexOf(region) === -1) continue;
        seen.add(s.handle);
        picks.push(s);
      }
      if (picks.length >= XS_MAX_TILES) break;
    }

    if (!picks.length) {
      _vjXsEmptyFor = cart.token;
      if (existing) existing.remove();
      return;
    }
    _vjXsEmptyFor = null;

    // Hydrate image + live price per tile (productCache makes repeats free).
    const prods = await Promise.all(
      picks.map(s => fetchProduct(vjSiblingPath(s.handle))));
    if (token !== _vjXsToken) return;

    const tiles = picks.map((s, i) => {
      const prod = prods[i];
      if (!prod) return '';
      const label = (typeof window.vjSynthesizeSiblingLabel === 'function'
        && window.vjSynthesizeSiblingLabel(s)) || s.label || prod.title || '';
      const img = vjXsImg(prod);
      const price = typeof prod.price === 'number'
        ? vjFormatMoney(prod.price, cart.currency || 'CAD') : '';
      const url = vjSiblingPath(s.handle);
      return [
        `<button type="button" class="vj-xs__tile" data-vj-quick-add data-product-url="${vjXsEsc(url)}">`,
        img ? `<img class="vj-xs__img" src="${vjXsEsc(img)}" alt="${vjXsEsc(label)}" loading="lazy" width="56" height="56">` : '',
        '<span class="vj-xs__meta">',
        `<span class="vj-xs__label">${vjXsEsc(label)}</span>`,
        price ? `<span class="vj-xs__price">${vjXsEsc(price)}</span>` : '',
        '</span>',
        '<span class="vj-xs__plus" aria-hidden="true">+</span>',
        '</button>',
      ].join('');
    }).join('');
    if (!tiles) { if (existing) existing.remove(); return; }

    const lang = vjLocale();
    const i18n = XS_I18N[lang] || XS_I18N.en;
    let row = existing;
    if (!row) {
      row = document.createElement('div');
      row.id = 'vj-cross-sell';
      row.className = 'vj-xs';
      const items = drawer.querySelector('cart-items-component');
      const host = items || drawer.querySelector('.cart-drawer__inner') || drawer;
      host.appendChild(row);
    }
    row.innerHTML = `<div class="vj-xs__title">${vjXsEsc(i18n.title)}</div>`
      + `<div class="vj-xs__tiles">${tiles}</div>`;
  }

  // Refresh on every cart change. Horizon dispatches this for adds, removes,
  // and quantity changes. Section morphing happens just before this fires, so
  // by the time we render the drawer DOM is already updated.
  document.addEventListener('cart:update', (e) => {
    const cart = e?.detail?.resource;
    // The event resource is a /cart/add.js response (single item) — not the
    // full cart. Always re-fetch /cart.js for the authoritative total.
    vjQueueShippingBarUpdate(cart && cart.token && cart.total_price !== undefined ? cart : null);
  });

  // ── Self-heal: keep the band from vanishing on drawer re-render ─────────────
  // Symptom: the band flashes in when you add a product, then disappears. Cause:
  // Horizon re-renders (morphs) the cart-drawer section right after the add —
  // and again on remove / quantity change / drawer-open — swapping the drawer's
  // inner DOM (and sometimes the drawer host itself), which silently drops our
  // injected #vj-shipping-bar. The debounced cart:update re-render races that
  // morph and usually loses; a plain drawer-open fires no cart:update at all.
  // So instead of relying on event timing, watch the document and re-inject from
  // the cached cart the instant the bar goes missing. Guarded by a fast id
  // lookup + rAF debounce, so observing at document scope costs ~nothing, and
  // it's cache-backed so there's no fetch.
  let _vjHealQueued = false;
  function vjHealShippingBar() {
    if (_vjHealQueued) return;
    _vjHealQueued = true;
    requestAnimationFrame(() => {
      _vjHealQueued = false;
      wireCartBackTrap();   // idempotent — (re)attach the Back-trap if the drawer mounts/morphs late
      if (!_vjLastCart) return;
      const drawerPresent = vjFindCartDrawer();
      if (!drawerPresent) return;
      if (!document.getElementById('vj-shipping-bar')) vjRenderShippingBar(_vjLastCart);
      // Cross-sell heal: same morph problem, same fix. _vjXsEmptyFor guards
      // the "row legitimately absent" case so an empty decision doesn't
      // re-run the async render on every DOM mutation.
      if (!document.getElementById('vj-cross-sell')
          && _vjXsEmptyFor !== _vjLastCart.token) {
        vjRenderCrossSell(_vjLastCart);
      }
    });
  }
  new MutationObserver(vjHealShippingBar).observe(document.documentElement, { childList: true, subtree: true });

  // ── Android/browser Back closes the cart drawer instead of leaving the site ──
  // The cart drawer is a native <dialog> (showModal), which has no history entry,
  // so the hardware Back button navigates away — the owner reported reflexively
  // exiting the site (QA 2026-07 MOBILE R7). Mobile-only: on open we push a
  // throwaway history entry so Back pops it and we close the drawer; on any other
  // close we remove that entry, so there's never a double-press. Only the cart
  // drawer is hooked (dialog:open/close are dispatched on the component itself,
  // so listening on it needs no bubbling and doesn't touch other dialogs).
  var _vjCartOpen = false, _vjCartTrapped = false;
  function wireCartBackTrap() {
    var c = document.querySelector('cart-drawer-component');
    if (!c || c._vjBackTrap) return;
    c._vjBackTrap = true;
    c.addEventListener('dialog:open', function () {
      _vjCartOpen = true;
      if (!_vjCartTrapped && window.matchMedia && window.matchMedia('(max-width: 749px)').matches) {
        try { history.pushState({ vjCartDrawer: 1 }, ''); _vjCartTrapped = true; } catch (e) {}
      }
    });
    c.addEventListener('dialog:close', function () {
      _vjCartOpen = false;
      if (_vjCartTrapped) { _vjCartTrapped = false; try { history.back(); } catch (e) {} }
    });
  }
  window.addEventListener('popstate', function () {
    if (_vjCartTrapped && _vjCartOpen) {   // Back pressed while the drawer is open
      _vjCartTrapped = false;              // our pushed entry was just consumed
      var c = document.querySelector('cart-drawer-component');
      if (c && typeof c.close === 'function') c.close();
    }
  });

  // Initial render on page load (catches the case where the drawer is opened
  // without an add — e.g. user clicks the cart icon directly).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { vjQueueShippingBarUpdate(); vjSetupColorFacetTranslation(); wireCartBackTrap(); });
  } else {
    vjQueueShippingBarUpdate();
    vjSetupColorFacetTranslation();
    wireCartBackTrap();
  }
})();
